import { format } from 'date-fns';
import { ReactNode, useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { TradesDataOptions, useTradesData } from '@/hooks';
import { TimeWindow } from '@/types';

import { ChartHeader, EmptyState, LoadingSpinner } from './ChartScaffold';

const defaultPriceTickFormatter = (value: number) =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const createLineYAxisConfig = (
  series: Array<{ price: number | null }>
): {
  domain: [number | 'auto', number | 'auto'];
  tickFormatter: (value: number) => string;
} => {
  if (!series || series.length === 0) {
    return {
      domain: ['auto', 'auto'],
      tickFormatter: defaultPriceTickFormatter,
    };
  }

  const prices = series
    .map((point) => point.price)
    .filter(
      (value): value is number =>
        typeof value === 'number' && !Number.isNaN(value)
    );

  if (prices.length === 0) {
    return {
      domain: ['auto', 'auto'],
      tickFormatter: defaultPriceTickFormatter,
    };
  }

  const min = Math.min(...prices);
  const max = Math.max(...prices);

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return {
      domain: ['auto', 'auto'],
      tickFormatter: defaultPriceTickFormatter,
    };
  }

  const rawRange = max - min;
  const magnitude = Math.max(Math.abs(min), Math.abs(max));
  const basePadding = rawRange === 0 ? magnitude * 0.1 : rawRange * 0.1;
  const padding =
    basePadding > 0
      ? basePadding
      : magnitude > 0
        ? Math.max(magnitude * 0.1, Number.EPSILON)
        : 0.1;

  const lowerBound = min >= 0 ? Math.max(0, min - padding) : min - padding;
  const upperBound = max + padding;
  const span = upperBound - lowerBound;
  const effectiveSpan = span > Number.EPSILON ? span : magnitude || 1;

  let decimals = 0;
  if (effectiveSpan < 0.0001) {
    decimals = 6;
  } else if (effectiveSpan < 0.001) {
    decimals = 5;
  } else if (effectiveSpan < 0.01) {
    decimals = 4;
  } else if (effectiveSpan < 0.1) {
    decimals = 3;
  } else if (effectiveSpan < 1) {
    decimals = 2;
  } else if (effectiveSpan < 10) {
    decimals = 1;
  } else {
    decimals = 0;
  }

  const numberFormatter = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });

  return {
    domain: [lowerBound, upperBound],
    tickFormatter: (value: number) => numberFormatter.format(value),
  };
};

export type PriceChartPoint = {
  date: string;
  fullDate?: Date;
  price: number | null;
};

interface PriceLineChartProps {
  data: PriceChartPoint[];
  isLoading: boolean;
  emptyStateMessage: string;
  emptyStateHint?: string | null;
  tooltipSeriesLabel: string;
  tooltipLabelFormatter?: (
    dataPoint?: PriceChartPoint,
    label?: string
  ) => string;
  tooltipValueFormatter?: (
    value: number,
    dataPoint?: PriceChartPoint
  ) => string;
  yAxisLabel: string;
}

const PriceLineChart = ({
  data,
  isLoading,
  emptyStateMessage,
  emptyStateHint,
  tooltipSeriesLabel,
  tooltipLabelFormatter,
  tooltipValueFormatter,
  yAxisLabel,
}: PriceLineChartProps) => {
  const yAxisConfig = useMemo(() => createLineYAxisConfig(data), [data]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!data || data.length === 0) {
    return <EmptyState message={emptyStateMessage} hint={emptyStateHint} />;
  }

  return (
    <div className="relative h-full w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5,
          }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tickFormatter={(value: string) => value.split(',')[0]}
            tick={{ fontSize: 10 }}
          />
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 10 }}
            domain={yAxisConfig.domain}
            tickFormatter={yAxisConfig.tickFormatter}
            label={{
              value: yAxisLabel,
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle', fontSize: 10 },
            }}
          />
          <Tooltip
            labelFormatter={(
              label,
              payload: { payload?: PriceChartPoint }[]
            ) => {
              const dataPoint = payload?.[0]?.payload as
                | PriceChartPoint
                | undefined;
              if (tooltipLabelFormatter) {
                return tooltipLabelFormatter(
                  dataPoint,
                  typeof label === 'string' ? label : undefined
                );
              }

              if (dataPoint?.fullDate) {
                return `Dată: ${format(dataPoint.fullDate, 'MMMM d, yyyy')}`;
              }

              return `Dată: ${label}`;
            }}
            formatter={(value: number, name: string, payload) => {
              const dataPoint = (payload?.payload ?? undefined) as
                | PriceChartPoint
                | undefined;
              const formatted = Number.isFinite(value)
                ? yAxisConfig.tickFormatter(value)
                : `${value}`;

              const finalValue = tooltipValueFormatter
                ? tooltipValueFormatter(value, dataPoint)
                : formatted;

              return [finalValue, tooltipSeriesLabel || name];
            }}
          />
          <Legend />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="price"
            stroke="#000000"
            strokeWidth={2}
            name={tooltipSeriesLabel}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export interface PriceChartProps {
  symbol: string;
  timeWindow: TimeWindow;
  onTimeWindowChange: (window: TimeWindow) => void;
  title: string;
  description: string;
  timeWindowLabels: Record<TimeWindow, string>;
  emptyStateMessage: string;
  tooltipSeriesLabel?: string;
  tooltipLabelFormatter?: (
    dataPoint?: PriceChartPoint,
    label?: string
  ) => string;
  tooltipValueFormatter?: (
    value: number,
    dataPoint?: PriceChartPoint
  ) => string;
  yAxisLabel?: string;
  footerNote?: ReactNode;
  footerClassName?: string;
  cardClassName?: string;
  chartContainerHeight?: string;
  queryOptions?: TradesDataOptions;
  emptyStateHint?: string | null;
  showTimeWindowControls?: boolean;
}

export const PriceChart = ({
  symbol,
  timeWindow,
  onTimeWindowChange,
  title,
  description,
  timeWindowLabels,
  emptyStateMessage,
  tooltipSeriesLabel = 'Preț',
  tooltipLabelFormatter,
  tooltipValueFormatter,
  yAxisLabel = 'Preț',
  footerNote,
  footerClassName = 'mt-2 text-xs text-neutral-500',
  cardClassName = 'shadow-md',
  chartContainerHeight = 'h-64',
  queryOptions,
  emptyStateHint,
  showTimeWindowControls = true,
}: PriceChartProps) => {
  const { data: tradesData, isLoading: tradesLoading } = useTradesData(
    symbol,
    timeWindow,
    queryOptions
  );

  const lineChartData = useMemo<PriceChartPoint[]>(() => {
    if (!tradesData) {
      return [];
    }

    if (
      !tradesData.trades ||
      !Array.isArray(tradesData.trades) ||
      tradesData.trades.length === 0
    ) {
      return [];
    }

    const rawData = tradesData.trades
      .map((trade) => {
        try {
          const timestamp = trade.time;
          if (!timestamp) {
            throw new Error('No timestamp found');
          }

          const tradeDate = new Date(timestamp);

          if (Number.isNaN(tradeDate.getTime())) {
            throw new Error('Invalid timestamp');
          }

          const dateFormat =
            timeWindow === '1day' ? 'HH:mm, MMM dd yyyy' : 'MMM dd yyyy';

          return {
            date: format(tradeDate, dateFormat),
            fullDate: tradeDate,
            price: trade.avg_price ?? trade.close ?? null,
          } as PriceChartPoint;
        } catch (error) {
          console.warn('Failed to process trade data:', trade, error);
          return null;
        }
      })
      .filter(Boolean) as PriceChartPoint[];

    return rawData;
  }, [tradesData, timeWindow]);

  return (
    <Card className={cardClassName}>
      <CardHeader>
        <ChartHeader
          title={title}
          description={description}
          timeWindow={timeWindow}
          onTimeWindowChange={onTimeWindowChange}
          labels={timeWindowLabels}
          showDateRange={false}
          showTimeWindowSelector={showTimeWindowControls}
        />
      </CardHeader>
      <CardContent>
        <div className={`${chartContainerHeight} w-full`}>
          <PriceLineChart
            data={lineChartData}
            isLoading={tradesLoading}
            emptyStateMessage={emptyStateMessage}
            emptyStateHint={emptyStateHint}
            tooltipSeriesLabel={tooltipSeriesLabel}
            tooltipLabelFormatter={tooltipLabelFormatter}
            tooltipValueFormatter={tooltipValueFormatter}
            yAxisLabel={yAxisLabel}
          />
        </div>
        {footerNote && <div className={footerClassName}>{footerNote}</div>}
      </CardContent>
    </Card>
  );
};
